import Label from "@marginly/ui/components/label";
import T from "@marginly/ui/components/typography";
import BigNumber from "bignumber.js";
import cn from "classnames";
import { useRouter } from "next/navigation";
import type { MouseEvent } from "react";
import React from "react";
import { useAccount } from "wagmi";

import { getTokenSymbol } from "@/app/util";
import { TradeActionButton as TradeActionButtonUi } from "@/components/trade/trade.styled";
import { ONE } from "@/constants/math";
import { TokenSide } from "@/contracts/util/types";
import { capitalize } from "@/util/core";
import { formatCoin } from "@/util/format-coin";

import { PositionDirection } from "./state";
import { ButtonState } from "./types";

interface Props {
  onClick: () => void;
  pending: boolean;
  buttonState: ButtonState;
  direction?: PositionDirection;
  positionSizeTotal?: BigNumber;
  basePrice?: BigNumber;
  baseSymbol?: string;
  positionDesc: React.ReactNode[];
  quoteSymbol?: string;
  needApprove: boolean;
  minPositionAmount?: BigNumber;
  minPositionSize?: BigNumber;
  withdrawSymbol?: string;
  shouldWithdraw: boolean;
  chainName?: string;
  token?: TokenSide;
}

const getPositionSizeModifier = (params: {
  direction?: PositionDirection;
  token?: TokenSide;
  basePrice?: BigNumber;
}) => {
  if (params.direction === "long") {
    return params.token === "base" ? ONE : params.basePrice?.pow(-1);
  } else {
    return params.token === "base" ? params.basePrice : ONE;
  }
};

export const TradeActionButton = ({
  onClick,
  pending,
  buttonState,
  direction,
  positionSizeTotal,
  basePrice,
  baseSymbol,
  quoteSymbol,
  positionDesc,
  needApprove,
  minPositionAmount,
  minPositionSize,
  withdrawSymbol,
  shouldWithdraw,
  chainName,
  token,
}: Props) => {
  const account = useAccount();

  const router = useRouter();

  function handleClick(e: MouseEvent) {
    if (buttonState === "lessThanMin") {
      e.stopPropagation();
    } else {
      return onClick();
    }
  }

  return (
    <TradeActionButtonUi
      onClick={(e: MouseEvent) => handleClick(e)}
      className={cn({
        pending,
        toSwap: ["notEnoughFunds", "lessThanMin"].includes(buttonState),
      })}
      style={{ display: "flex", justifyContent: "space-between" }}
    >
      {buttonState === "lessThanMin" && (
        <>
          <div>
            <div className="action__title">Position amount too low</div>
            <div className="action__subinfo">
              Increase leverage or deposit at least{" "}
              {formatCoin(minPositionSize)}{" "}
              {token === "base" ? baseSymbol : quoteSymbol}
            </div>
          </div>
        </>
      )}

      {buttonState === "deposit" && (
        <>
          <div>
            <div className="action__title">
              {capitalize(direction)}{" "}
              {formatCoin(
                positionSizeTotal?.times(
                  getPositionSizeModifier({ direction, token, basePrice }) ??
                    ONE,
                ),
              )}{" "}
              {getTokenSymbol(baseSymbol)}
            </div>
            <div className="action__subinfo">
              <>
                {!account.isConnected ? (
                  <>Sign in to trade</>
                ) : positionDesc[0] === null ? (
                  <>
                    {direction === "long" ? (
                      <>
                        with{" "}
                        {getTokenSymbol(
                          token === "base" ? baseSymbol : quoteSymbol,
                        )}{" "}
                        + {getTokenSymbol(quoteSymbol)}
                      </>
                    ) : (
                      <>
                        for{" "}
                        {getTokenSymbol(
                          token === "base" ? baseSymbol : quoteSymbol,
                        )}{" "}
                        + {getTokenSymbol(baseSymbol)}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {positionDesc[0]}&nbsp;
                    <span>{positionDesc[1]}</span>
                  </>
                )}
              </>
            </div>
          </div>
          {needApprove ? (
            <img
              src="/images/approve.svg"
              alt=""
              style={{ filter: "invert(var(--svg-invert))" }}
            />
          ) : (
            <img
              src="/images/arrow-right.svg"
              alt=""
              style={{ filter: "invert(var(--svg-invert))" }}
            />
          )}
        </>
      )}
      {shouldWithdraw && (
        <>
          {minPositionAmount?.gt(0) ? (
            <div>
              <div className="action__title">
                You already lend {formatCoin(minPositionAmount)}
                &nbsp;
                {getTokenSymbol(withdrawSymbol)}
              </div>
              <div className="action__subinfo">
                Withdraw all your {getTokenSymbol(withdrawSymbol)} or open your
                position with at least {formatCoin(minPositionAmount)}
                &nbsp;
                {buttonState === "shouldWithdrawBase"
                  ? baseSymbol
                  : quoteSymbol}
              </div>
            </div>
          ) : (
            <div>
              <div className="action__title">You already lend</div>
              <div className="action__subinfo">
                Withdraw all your {withdrawSymbol}
              </div>
            </div>
          )}
          <div className="action__subbutton">Withdraw</div>
        </>
      )}
    </TradeActionButtonUi>
  );
};
